// https://chakra-ui.com/docs/styled-system/theming/customize-theme
// eslint-disable-next-line @nx/enforce-module-boundaries
import { extendTheme } from '@chakra-ui/react';
import { createDlsTheme } from '@joinrepublic/dls-ui-theme';

import { Accordion } from './components/accordion';
import { Alert } from './components/alert';
import { Avatar } from './components/avatar';
import { Badge } from './components/badge';
import { Button } from './components/button';
import { Checkbox } from './components/checkbox';
import { FormLabel } from './components/form-label';
import { Heading } from './components/heading';
import { Input } from './components/input';
import { Link } from './components/link';
import { Modal } from './components/modal';
import { Popover } from './components/popover';
import { Progress } from './components/progress';
import { Radio } from './components/radio';
import { Select } from './components/select';
import { Skeleton } from './components/skeleton';
import { Spinner } from './components/spinner';
import { Switch } from './components/switch';
import { Tabs } from './components/tabs';
import { Text } from './components/text';
import { Textarea } from './components/textarea';
import { Tooltip } from './components/tooltip';
// FOUNDATIONS:
import { radii } from './foundations/border-radius';
import { breakpoints } from './foundations/breakpoints';
import { colors } from './foundations/colors';
import { layerStyles } from './foundations/layer-styles';
import { space } from './foundations/spacing';
import {
  fonts,
  fontSizes,
  letterSpacings,
  lineHeights,
  textStyles,
} from './foundations/typography';

export * from './utilities/px-to-number';

export const legacyTheme = extendTheme({
  breakpoints,
  colors,
  fonts,
  fontSizes,
  letterSpacings,
  lineHeights,
  layerStyles,
  radii,
  space,
  textStyles,
  components: {
    Accordion,
    Alert,
    Avatar,
    Badge,
    Button,
    Checkbox,
    FormLabel,
    Heading,
    Input,
    Link,
    Modal,
    Popover,
    Progress,
    Radio,
    Select,
    Skeleton,
    Spinner,
    Switch,
    Tabs,
    Text,
    Textarea,
    Tooltip,
  },
});

const globalStyles = {
  styles: {
    global: {
      'html, body': {
        // The new "Inter Var" font introduces optical sizing control.
        // By default, it is set to "auto", which makes the font appear tighter than before.
        // Setting it to 14 restores the previous appearance.
        fontVariationSettings: "'opsz' 14",
      },
      '.pac-container': {
        // Needed by LocationSuggestInput (uses react-google-maps)
        zIndex: '9999',
      },
    },
  },
};

const dlsTheme = createDlsTheme();

// Prevent Chakra's inherited semanticTokens from leaking into the merged theme;
// delete is a no-op when the property is absent, so the guard is unnecessary.
delete legacyTheme.semanticTokens;

/**
 * theme with legacy support (deprecated)
 * @deprecated Use `theme` instead if the page only imports components from `@joinrepublic/dls-ui-kit`. Keep using `republicLegacy` if the page still uses direct imports from `@chakra-ui/react` (please refactor those).
 */
export const republicLegacy = extendTheme(globalStyles, legacyTheme, dlsTheme);

// theme from DLS (use this for new pages)
export const theme = extendTheme(globalStyles, dlsTheme);

export const embeddedTheme = extendTheme(republicLegacy, {
  styles: {
    global: {
      'html, body': {
        height: 'auto',
        overflow: 'hidden',
      },
    },
  },
});

// chakra-cli reads mod.default || mod.theme — export republicLegacy as default
// so the generated theme.d.ts uses the full legacy+DLS token set
// eslint-disable-next-line import/no-default-export
export default republicLegacy;
